import { getVariantDisplayQuantity } from '../../../../../utils/variant-display';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrandLink, CMSSection, CategoryLink, CollectionBannerConfig } from '../../types';
import { CategoriesService, CategoryDto } from '../../../../../services/categories/categories.service';
import { ProductsService, Product } from '../../../../../services/products/products.service';
import { Brand, BrandService } from '../../../../../services/brand/brand.service';
import { CollectionsService, Collection } from '../../../../../services/collections/collections.service';
import { ImageUploadType } from '../../../../../config/imageUploadConfig';
import { ImageUploaderComponent, ValidImageResult } from '../../../../../Design/image-uploader/image-uploader.component';

interface FlatCategory {
  id: number;
  name: string;
  level: number;
}

@Component({
  selector: 'app-section-config',
  standalone: true,
  imports: [CommonModule, FormsModule, ImageUploaderComponent],
  templateUrl: './section-config.component.html',
  styles: [`
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  `]
})
export class SectionConfigComponent implements OnChanges {
  @Input() section: CMSSection | undefined;

  /** Emitted whenever any config field changes (does NOT trigger DB save; parent marks dirty). */
  @Output() configChanged = new EventEmitter<void>();

  private categoriesService = inject(CategoriesService);
  private productsService = inject(ProductsService);
  private brandService = inject(BrandService);
  private collectionsService = inject(CollectionsService);
  
  @Input() availableCategories: CategoryDto[] = [];
  flatCategories: FlatCategory[] = [];
  availableBrands: Brand[] = [];
  availableCollections: Collection[] = [];
  productResults: Product[] = [];
  manualProducts: Product[] = [];
  productSearch = '';
  categorySearch = '';
  brandSearch = '';
  isUploadingImage = false;
  uploadingBannerIndex: number | null = null;
  uploadingVisualKey = '';
  readonly collectionLayouts = [
    { value: 'hero_mosaic', label: 'Tall + Mosaic', previewClass: 'flex-row' },
    { value: 'overlay_cards', label: 'Card Row', previewClass: 'flex-row' },
    { value: 'bento_grid', label: 'Decor Grid', previewClass: 'grid grid-cols-3' },
    { value: 'horizontal_carousel', label: 'Peek Cards', previewClass: 'flex-row' },
    { value: 'left_hero', label: 'Tall + Stack', previewClass: 'flex-row' },
    { value: 'glassmorphism', label: 'Soft Grid', previewClass: 'grid grid-cols-2' },
    { value: 'spotlight', label: 'Spotlight Strip', previewClass: 'flex-col' },
    { value: 'product_picks', label: 'Festive Picks', previewClass: 'flex-row' },
    { value: 'category_essentials', label: 'Category Essentials', previewClass: 'flex-col' },
  ];
  readonly collectionTextStyles = [
    { value: 'teej', label: 'Teej caption' },
    { value: 'overlay', label: 'Photo overlay' },
    { value: 'caption', label: 'Bottom bar' },
    { value: 'pill', label: 'Corner pill' },
  ];
  readonly collectionOfferDesigns = [
    { value: 'card', label: 'Offer card' },
    { value: 'ribbon', label: 'Ribbon' },
    { value: 'powered', label: 'Powered by' },
    { value: 'ticket', label: 'Ticket' },
  ];
  readonly collectionProductStyles = [
    { value: 'product_card', label: 'Product list' },
    { value: 'compact', label: 'Compact tile' },
    { value: 'poster', label: 'Poster card' },
    { value: 'deal', label: 'Deal card' },
  ];
  readonly collectionChipStyles = [
    { value: 'circle', label: 'Spotlight', description: 'Round image with an active card' },
    { value: 'pill', label: 'Capsule', description: 'Compact image and label' },
    { value: 'tile', label: 'Soft tile', description: 'Large image-led category' },
    { value: 'bar', label: 'Minimal', description: 'Text-only filter chips' },
    { value: 'framed', label: 'Editorial', description: 'Framed image cards' },
  ];
  readonly collectionBorderStyles = [
    { value: 'scallop', label: 'Scallop lace' },
    { value: 'wave', label: 'Soft wave' },
    { value: 'petal', label: 'Temple lace' },
  ];
  readonly productShelfStyles = [
    { value: 'grid', label: 'Product Grid' },
    { value: 'two_row', label: '2-Row Product List' },
  ];

  get isCollectionProductLayout(): boolean {
    const layout = this.configData.layout_type;
    return layout === 'product_picks' || layout === 'category_essentials';
  }

  getCollectionBorderThumbStyle(kind: string, side: 'top' | 'bottom' = 'top'): Record<string, string> {
    const color =
      side === 'bottom'
        ? this.configData.bottom_border_color || this.configData.border_color || '#FFF6E4'
        : this.configData.top_border_color || this.configData.border_color || '#FFF6E4';
    const under =
      side === 'bottom'
        ? this.configData.bottom_border_under_color || this.configData.border_under_color || '#FFFFFF'
        : this.configData.top_border_under_color || this.configData.border_under_color || '#FFFFFF';
    return {
      height: '12px',
      backgroundColor: under,
      backgroundImage:
        kind === 'petal'
          ? 'radial-gradient(circle at 5px 0, ' + color + ' 5px, transparent 5.5px)'
          : kind === 'wave'
            ? 'radial-gradient(circle at 8px 0, ' + color + ' 8px, transparent 8.5px)'
            : 'radial-gradient(circle at 4px 0, ' + color + ' 4px, transparent 4.5px)',
      backgroundSize: kind === 'wave' ? '16px 12px' : kind === 'petal' ? '10px 12px' : '8px 12px',
      backgroundRepeat: 'repeat-x',
    };
  }

  collectionBorderStyleValue(side: 'top' | 'bottom'): string {
    return side === 'bottom'
      ? this.configData.bottom_border_style || this.configData.border_style || 'scallop'
      : this.configData.top_border_style || this.configData.border_style || 'scallop';
  }

  setCollectionBorderStyle(side: 'top' | 'bottom', value: string) {
    if (side === 'bottom') this.configData.bottom_border_style = value;
    else this.configData.top_border_style = value;
    this.emit();
  }

  get visualCardUploadType(): ImageUploadType {
    if (this.section?.type === 'featured_cards') return 'FEATURED_CARD';
    if (this.section?.type === 'promo_carousel') return 'PROMO_CARD_ART';
    return 'COLLECTION_TILE_ART';
  }

  constructor() {
    this.brandService.listBrands({ limit: 100, sortBy: 'name', order: 'ASC' }).subscribe({
      next: brands => this.availableBrands = brands,
      error: () => this.availableBrands = [],
    });
    this.collectionsService.getCollections().subscribe({
      next: collections => this.availableCollections = collections,
      error: () => this.availableCollections = [],
    });
    this.searchProducts('');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['section']) {
      this.ensureConfigDefaults();
      this.syncManualProducts();
    }
    if (changes['availableCategories'] && this.availableCategories) {
      this.flatCategories = this.flattenCategories(this.availableCategories);
    }
  }

  private flattenCategories(cats: CategoryDto[], level = 0): FlatCategory[] {
    let result: FlatCategory[] = [];
    cats.forEach(cat => {
      result.push({ id: cat.id, name: cat.name, level });
      if (cat.children && cat.children.length > 0) {
        result = [...result, ...this.flattenCategories(cat.children, level + 1)];
      }
    });
    return result;
  }

  getCategoryIndent(level: number): string {
    return '\u00A0'.repeat(level * 3);
  }

  get configData(): any {
    if (!this.section) return {};
    if (!this.section.config) this.section.config = { data: {}, version: 1 };
    if (!this.section.config.data) this.section.config.data = {};
    this.section.config.data = this.unwrapConfigData(this.section.config.data);
    return this.section.config.data;
  }

  private unwrapConfigData(config: any): any {
    let data = config?.data ?? config ?? {};

    while (
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      data.data &&
      typeof data.data === 'object' &&
      Object.keys(data).every((key) => key === 'data' || key === 'version')
    ) {
      data = data.data;
    }

    return data || {};
  }

  // ── Banner Management ──────────────────────────────────────────────────────
  addBanner() {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    if (!data.banners) data.banners = [];
    data.banners.push({
      image_url: '',
      image_file_id: null,
      image_bucket: '',
      image_path: '',
      title:     'New Highlight',
      title_color: '#ffffff',
      subtitle: '',
      subtitle_color: '#ffffff',
      cta_text:  'Shop Now',
      cta_bg_color: '#000000',
      cta_text_color: '#ffffff',
      link_url:  '',
    });
    this.emit();
  }

  onBannerFileSelected(files: ValidImageResult[], banner: any, index: number) {
    if (!files.length || !this.section) return;
    const file = files[0].file;

    banner.image_url = files[0].url;
    banner.image_file_id = null;
    banner.image_bucket = '';
    banner.image_path = '';
    banner._pending_file = file;
    banner._pending_field = 'image_url';
    banner._pending_upload_key = `banner-${index}`;
    this.emit();
  }

  onVisualFileSelected(files: ValidImageResult[], target: any, key = 'image', field = 'image_url') {
    if (!files.length || !this.section) return;
    const file = files[0].file;

    target[field] = files[0].url;
    target._pending_file = file;
    target._pending_field = field;
    target._pending_upload_key = key;
    this.emit();
  }

  ensureOfferImageSlots() {
    const offer = this.configData.bottom_offer;
    if (!offer) return;
    if (!Array.isArray(offer.images)) {
      offer.images = [];
    }
    while (offer.images.length < 3) {
      offer.images.push({ image_url: '' });
    }
    offer.images = offer.images.slice(0, 3);
    if (!offer.images[0].image_url && offer.image_url) {
      offer.images[0].image_url = offer.image_url;
    }
    offer.image_url = this.firstOfferImageUrl(offer.images);
  }

  private firstOfferImageUrl(
    images: Array<{ image_url?: string }> | undefined,
  ): string {
    return images?.find((image: { image_url?: string }) => image.image_url)?.image_url || '';
  }

  onOfferImageSelected(files: ValidImageResult[], index: number) {
    this.ensureOfferImageSlots();
    const slot = this.configData.bottom_offer.images[index];
    this.onVisualFileSelected(files, slot, `offer-image-${index}`);
    this.configData.bottom_offer.image_url = this.firstOfferImageUrl(
      this.configData.bottom_offer.images,
    );
  }

  clearOfferImage(index: number) {
    this.ensureOfferImageSlots();
    const slot = this.configData.bottom_offer.images[index];
    slot.image_url = '';
    delete slot._pending_file;
    delete slot._pending_field;
    delete slot._pending_upload_key;
    this.configData.bottom_offer.image_url = this.firstOfferImageUrl(
      this.configData.bottom_offer.images,
    );
    this.emit();
  }

  removeBanner(index: number) {
    this.configData.banners?.splice(index, 1);
    this.emit();
  }

  moveBanner(from: number, to: number) {
    const arr = this.configData.banners;
    if (!arr || to < 0 || to >= arr.length) return;
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    this.emit();
  }

  // ── Category Management ───────────────────────────────────────────────────
  addCategory() {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    if (!data.categories) data.categories = [];
    data.categories.push({ category_id: '', name: 'New Category', image_url: '' });
    this.emit();
  }

  removeCategory(index: number) {
    this.configData.categories?.splice(index, 1);
    this.emit();
  }

  get filteredAvailableCategories(): CategoryDto[] {
    const term = this.categorySearch.trim().toLowerCase();
    return this.availableCategories.filter(category =>
      category.isActive !== false && (!term || category.name.toLowerCase().includes(term))
    );
  }

  isCategorySelected(categoryId: number): boolean {
    return !!this.configData.categories?.some(
      (category: CategoryLink) => category.category_id === String(categoryId)
    );
  }

  selectCategory(category: CategoryDto) {
    if (this.isCategorySelected(category.id)) return;
    this.configData.categories ??= [];
    this.configData.categories.push({
      category_id: String(category.id),
      name: category.name,
      image_url: category.iconUrl || '',
    });
    this.emit();
  }

  getCategoryChildCount(categoryId: string): number {
    return this.findCategoryById(this.availableCategories, Number(categoryId))?.children
      ?.filter(child => child.isActive !== false).length || 0;
  }

  moveCategory(from: number, to: number) {
    this.moveConfigItem(this.configData.categories, from, to);
  }

  onCategorySelect(cat: CategoryLink, event: any) {
    const categoryId = event.target.value;
    const selected = this.findCategoryById(this.availableCategories, Number(categoryId));
    if (selected) {
      cat.category_id = String(selected.id);
      cat.name        = selected.name;
      cat.image_url   = selected.iconUrl || '';
      this.emit();
    }
  }

  private findCategoryById(cats: CategoryDto[], id: number): CategoryDto | undefined {
    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = this.findCategoryById(cat.children, id);
        if (found) return found;
      }
    }
    return undefined;
  }

  onBrandSelect(brandLink: BrandLink, event: any) {
    const brandId = event.target.value;
    const selected = this.availableBrands.find(b => b.id === Number(brandId));
    if (selected) {
      brandLink.brand_id = String(selected.id);
      brandLink.name = selected.name;
      brandLink.image_url = selected.logo || selected.icon?.url || '';
      this.emit();
    }
  }

  get filteredAvailableBrands(): Brand[] {
    const term = this.brandSearch.trim().toLowerCase();
    return this.availableBrands.filter(brand =>
      brand.status !== 'archived' && (!term || brand.name.toLowerCase().includes(term))
    );
  }

  isBrandSelected(brandId: number): boolean {
    return !!this.configData.brands?.some(
      (brand: BrandLink) => brand.brand_id === String(brandId)
    );
  }

  selectBrand(brand: Brand) {
    if (this.isBrandSelected(brand.id)) return;
    this.configData.brands ??= [];
    this.configData.brands.push({
      brand_id: String(brand.id),
      name: brand.name,
      image_url: brand.logo || brand.icon?.url || '',
    });
    this.emit();
  }

  moveBrand(from: number, to: number) {
    this.moveConfigItem(this.configData.brands, from, to);
  }

  onDeepLinkCategorySelect(target: any, event: any) {
    const categoryId = event.target.value;
    if (categoryId) {
      target.link_url = `/category/${categoryId}`;
    } else {
      target.link_url = '';
    }
    this.emit();
  }

  isCategorySelectedForDeepLink(target: any, categoryId: number): boolean {
    return target.link_url === `/category/${categoryId}`;
  }

  onDeepLinkCollectionSelect(target: any, event: any) {
    const slug = event.target.value;
    if (slug) {
      target.link_url = `/collection/${slug}`;
    } else {
      target.link_url = '';
    }
    this.emit();
  }

  isCollectionSelectedForDeepLink(target: any, slug: string | undefined): boolean {
    return !!slug && target.link_url === `/collection/${slug}`;
  }

  onLinkTypeChange(target: any) {
    target.link_url = '';
    this.emit();
  }

  // ── Shelf Management ──────────────────────────────────────────────────────
  onShelfTypeChange() {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    if (data.data_source.type === 'manual') {
      data.data_source.product_ids ??= [];
      data.data_source.products ??= [];
      this.syncManualProducts();
    } else {
      data.data_source.query ??= { limit: 10, sort_by: 'featured' };
    }
    this.emit();
  }

  searchProducts(term: string) {
    this.productSearch = term;
    this.productsService.searchOrLatestProducts(term, term ? 10 : 8).subscribe({
      next: products => {
        this.productResults = products;
      },
      error: () => {
        this.productResults = [];
      },
    });
  }

  addManualProduct(product: Product) {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    const source = data.data_source;
    source.type = 'manual';
    source.product_ids ??= [];

    if (!source.product_ids.includes(product.id)) {
      source.product_ids.push(product.id);
      source.products ??= [];
      source.products.push(this.toProductSnapshot(product));
      this.manualProducts.push(product);
      this.emit();
    }
  }

  removeManualProduct(productId: number) {
    const data = this.configData;
    if (!data.data_source?.product_ids) return;
    data.data_source.product_ids = data.data_source.product_ids.filter((id: number) => id !== productId);
    data.data_source.products = (data.data_source.products || []).filter((product: any) => product.id !== productId);
    this.manualProducts = this.manualProducts.filter(product => product.id !== productId);
    this.emit();
  }

  moveManualProduct(from: number, to: number) {
    const source = this.configData.data_source;
    if (!source || to < 0 || to >= this.manualProducts.length) return;
    const [product] = this.manualProducts.splice(from, 1);
    this.manualProducts.splice(to, 0, product);
    source.product_ids = this.manualProducts.map(item => item.id);
    const snapshots = source.products || [];
    source.products = source.product_ids
      .map((id: number) => snapshots.find((item: any) => item.id === id))
      .filter(Boolean);
    this.emit();
  }

  isManualProductSelected(productId: number): boolean {
    return !!this.configData.data_source?.product_ids?.includes(productId);
  }

  getPrimaryProductImage(product: Product): string {
    return product.images?.find(image => image.isPrimary)?.url || product.images?.[0]?.url || '';
  }

  getPrimaryVariantLabel(product: Product): string {
    const variant = product.variants?.[0];
    if (!variant) return 'Unit';
    return getVariantDisplayQuantity(variant) || 'Unit';
  }

  getPrimaryVariantPrice(product: Product): number | string {
    return product.variants?.[0]?.price ?? '-';
  }

  addVisualItem(collectionName: 'items' | 'cards') {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    data[collectionName] ??= [];
    data[collectionName].push({
      title: 'New Card',
      subtitle: '',
      badge: '',
      cta_text: collectionName === 'cards' ? 'Shop now' : '',
      image_url: '',
      background_color: collectionName === 'cards' ? '#f8ddf7' : '#ecfff7',
      border_color: '#2873df',
      text_color: '#111111',
      link_url: '',
    });
    this.emit();
  }

  addBrand() {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    data.brands ??= [];
    data.brands.push({ brand_id: '', name: 'New Brand', image_url: '' });
    this.emit();
  }

  removeBrand(index: number) {
    this.configData.brands?.splice(index, 1);
    this.emit();
  }

  addHeroCard() {
    if (!this.section) return;
    this.ensureConfigDefaults();
    const data = this.configData;
    data.cards ??= [];
    data.cards.push({
      title: 'New Card',
      badge: '',
      image_url: '',
      background_color: '#ffffff',
      link_url: '',
    });
    this.emit();
  }

  removeHeroCard(index: number) {
    this.configData.cards?.splice(index, 1);
    this.emit();
  }

  removeVisualItem(collectionName: 'items' | 'cards', index: number) {
    this.configData[collectionName]?.splice(index, 1);
    this.emit();
  }

  moveVisualItem(collectionName: 'items' | 'cards', from: number, to: number) {
    this.moveConfigItem(this.configData[collectionName], from, to);
  }

  moveHeroCard(from: number, to: number) {
    this.moveConfigItem(this.configData.cards, from, to);
  }

  private moveConfigItem(items: any[] | undefined, from: number, to: number) {
    if (!items || to < 0 || to >= items.length) return;
    const [item] = items.splice(from, 1);
    items.splice(to, 0, item);
    this.emit();
  }

  private syncManualProducts() {
    const data = this.configData;
    const ids = data.data_source?.product_ids || [];
    if (!ids.length) {
      this.manualProducts = [];
      return;
    }

    const known = [...this.manualProducts, ...this.productResults]
      .filter(product => ids.includes(product.id));
    const missingIds = ids.filter((id: number) => !known.some(product => product.id === id));
    this.manualProducts = ids
      .map((id: number) => known.find(product => product.id === id))
      .filter(Boolean) as Product[];

    missingIds.slice(0, 12).forEach((id: number) => {
      this.productsService.getProductById(id).subscribe({
        next: product => {
          if (!this.manualProducts.some(item => item.id === product.id)) {
            this.manualProducts = [...this.manualProducts, product];
          }
        },
      });
    });
  }

  private toProductSnapshot(product: Product) {
    return {
      id: product.id,
      name: product.name,
      brand: product.brand?.name || '',
      unit: this.getPrimaryVariantLabel(product),
      price: this.getPrimaryVariantPrice(product),
      image_url: this.getPrimaryProductImage(product),
      badge: this.configData.discount_label || '25% OFF',
      rating: '4.5',
      inventory_label: '',
    };
  }

  private ensureConfigDefaults() {
    if (!this.section) return;
    if (!this.section.config || !this.section.config.data) {
      // If config is flat or missing, migrate to nested 'data'
      const oldConfig = this.section.config || {};
      if (oldConfig.data) {
        // already has data, but maybe version is missing
        this.section.config.version ??= 1;
        this.section.config.data = this.unwrapConfigData(oldConfig.data);
      } else {
        this.section.config = {
          data: this.unwrapConfigData(oldConfig),
          version: 1
        };
      }
    }
    
    const data = this.section.config.data;

    if (this.section.type === 'banner') {
      data.auto_scroll ??= true;
      data.scroll_interval_ms ??= 3000;
      data.aspect_ratio ??= '21:9';
      data.banners ??= [];
      
      // Ensure existing banners have style defaults
      data.banners.forEach((b: any) => {
        b.title_color ??= '#ffffff';
        b.subtitle ??= '';
        b.subtitle_color ??= '#ffffff';
        b.cta_bg_color ??= '#000000';
        b.cta_text_color ??= '#ffffff';
      });
    }

    if (this.section.type === 'category_grid') {
      data.title ??= 'Shop by Category';
      data.columns ??= 4;
      data.show_labels ??= true;
      data.categories ??= [];
    }

    if (this.section.type === 'brand_grid') {
      data.title ??= 'Shop by Brands';
      data.brands ??= [];
    }

    if (this.section.type === 'offer_strip') {
      data.title ??= 'Extra 10% OFF';
      data.subtitle ??= 'Use code SUPIDOO10';
      data.background_color ??= '#fffbeb';
      data.text_color ??= '#92400e';
      data.cta_text ??= 'Copy';
      data.ui_type ??= 'classic';
      data.icon ??= 'fi-rs-ticket';
    }

    if (this.section.type === 'product_shelf') {
      data.title ??= 'Must Try Items';
      if (data.ui_type === 'horizontal' || data.ui_type === 'deal') {
        data.ui_type = 'grid';
      }
      data.ui_type ??= 'grid';
      data.show_see_all ??= true;
      data.data_source ??= { type: 'query', query: { limit: 10, sort_by: 'featured' } };
      data.data_source.type ??= 'query';
      data.data_source.query ??= { limit: 10, sort_by: 'featured' };
      data.data_source.query.limit ??= 10;
      data.data_source.query.sort_by ??= 'featured';
      data.data_source.product_ids ??= [];
      data.data_source.products ??= [];
      data.background_color ??= '#dff1ff';
      data.text_color ??= '#000000';
      data.indicator_icon ??= 'fi-rs-percentage';
      data.discount_label ??= '25% OFF';
    }

    if (this.section.type === 'collection_banner') {
      data.title ??= 'Salon';
      data.subtitle ??= 'at Home';
      data.background_type ??= 'solid';
      data.background_color ??= '#7A2E0E';
      data.background_color_end ??= '#F6D7A4';
      data.gradient_angle ??= 180;
      data.background_image_url ??= '';
      data.title_color ??= data.text_color ?? '#5A2A0A';
      data.subtitle_color ??= data.text_color ?? '#5A2A0A';
      data.text_color = data.title_color;
      data.text_align ??= 'center';
      data.layout_type ??= 'hero_mosaic';
      data.render_main_banner ??= true;
      data.edge_to_edge ??= true;
      data.show_top_border ??= false;
      data.show_bottom_border ??= false;
      data.border_style ??= 'scallop';
      data.border_color ??= '#FFF6E4';
      data.border_under_color ??= '#FFFFFF';
      data.top_border_style ??= data.border_style;
      data.bottom_border_style ??= data.border_style;
      data.top_border_color ??= data.border_color;
      data.bottom_border_color ??= data.border_color;
      data.top_border_under_color ??= data.border_under_color;
      data.bottom_border_under_color ??= data.border_under_color;
      data.text_style ??= 'teej';
      data.product_style ??= 'product_card';
      data.product_display_style ??= 'grid';
      data.chip_style ??= 'circle';
      data.chip_selected_text_color ??= '#B45309';
      data.chip_unselected_text_color ??= '#5A2A0A';
      data.chip_selected_border_color ??= '#B45309';
      data.show_bottom_offer ??= false;
      data.data_source ??= { type: 'query', query: { limit: 8, sort_by: 'featured' }, products: [], product_ids: [] };
      data.data_source.type ??= 'query';
      data.data_source.query ??= { limit: 8, sort_by: 'featured' };
      data.categories ??= [];
      data.main_banner ??= {
        title: 'Salon',
        subtitle: 'at Home',
        brand: 'Vatika',
        cta_text: 'Hair Routine',
        image_url: '',
        link_url: ''
      };
      data.items ??= [
        { title: 'DEALS', subtitle: 'Corner', badge: '₹699', mrp: '₹795', image_url: '', background_color: '#fff' },
        { title: 'Premium Hair Care Range', badge: 'STARTING ₹149', image_url: '', background_color: '#fff' },
        { title: 'Daily Hair Care', badge: 'STARTS AT ₹139', image_url: '', background_color: '#fff' },
        { title: 'Hair Colour & Styling', badge: 'STARTS AT ₹35', image_url: '', background_color: '#fff' },
        { title: 'Hair Growth & Supplement', badge: 'UP TO 60% OFF', image_url: '', background_color: '#fff' }
      ];
      data.bottom_offer ??= {
        title: 'Buy 2 get Upto 15% Off',
        subtitle: 'Loreal, Garnier & L’oreal Professional, *T & C applied',
        image_url: '',
        design: 'card',
        images: [{ image_url: '' }, { image_url: '' }, { image_url: '' }],
      };
      data.bottom_offer.design ??= 'card';
      this.ensureOfferImageSlots();
    }

    if (this.section.type === 'featured_cards') {
      data.title ??= 'Featured this week';
      data.card_style ??= 'rounded_square';
      data.items ??= [];
    }

    if (this.section.type === 'promo_carousel') {
      data.title ??= '';
      data.cards ??= [];
    }

    if (this.section.type === 'hero_showcase') {
      data.layout_type ??= 'banner_top_cards_bottom';
      data.ui_type ??= 'premium';
      data.container ??= {
        background_color: '#EEF2FF',
        border_radius: 24,
        padding: 16,
        shadow: true
      };
      data.banner ??= {
        title: 'Summer Electronics',
        subtitle: 'Up to 75% OFF',
        cta_text: 'Shop Now',
        image_url: '',
        link_url: ''
      };
      data.cards ??= [];
    }
  }

  emit() {
    if (this.section?.type === 'collection_banner' && this.configData?.title_color) {
      this.configData.text_color = this.configData.title_color;
    }
    this.configChanged.emit();
  }
}
